import React, { useState, useEffect } from 'react';
import { firestore } from '../firebase/config';
import { collection, getDocs, query, where, addDoc, getDoc, doc, setDoc } from 'firebase/firestore';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from '../firebase/config';
import { useToast } from './ToastContext';

const StudentQuiz = ({ courseName, studentId, studentUid, onQuizCompleted }) => {
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [quizStarted, setQuizStarted] = useState(false);
  const [quizCompleted, setQuizCompleted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [questionsLoading, setQuestionsLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [quizName, setQuizName] = useState('');
  const [user] = useAuthState(auth);
  const { showSuccess, showError } = useToast();
  
  // Timer and answer feedback states
  const [timeLeft, setTimeLeft] = useState(30);
  const [showAnswerFeedback, setShowAnswerFeedback] = useState(false);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [isAnswerCorrect, setIsAnswerCorrect] = useState(false);

  // Retake cooldown states
  const [retakeAvailableAt, setRetakeAvailableAt] = useState(null); // ISO string
  const [retakeRemainingMs, setRetakeRemainingMs] = useState(0);

  useEffect(() => {
    if (courseName && studentUid) {
      loadQuestions();
    }
  }, [courseName, studentUid]);

  // Separate useEffect for checking previous attempts after quiz is loaded
  useEffect(() => {
    if (courseName && studentUid && quizName) {
      checkPreviousAttempts();
    }
  }, [courseName, studentUid, quizName]);

  // Timer effect
  useEffect(() => {
    let timer;
    if (quizStarted && !quizCompleted && timeLeft > 0 && !showAnswerFeedback) {
      timer = setTimeout(() => {
        setTimeLeft(prev => prev - 1);
      }, 1000);
    } else if (timeLeft === 0 && !showAnswerFeedback) {
      // Time's up - mark question as wrong and move to next
      handleTimeUp();
    }
    return () => clearTimeout(timer);
  }, [timeLeft, quizStarted, quizCompleted, showAnswerFeedback]);

  // Reset timer when question changes
  useEffect(() => {
    if (quizStarted && !quizCompleted) {
      setTimeLeft(30);
      setShowAnswerFeedback(false);
      setSelectedAnswer(null);
    }
  }, [currentQuestionIndex, quizStarted, quizCompleted]);

  // Retake countdown effect
  useEffect(() => {
    if (!retakeAvailableAt) {
      setRetakeRemainingMs(0);
      return;
    }
    const target = new Date(retakeAvailableAt).getTime();
    const tick = () => {
      const ms = Math.max(0, target - Date.now());
      setRetakeRemainingMs(ms);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [retakeAvailableAt]);

  const formatDuration = (ms) => {
    const totalSeconds = Math.ceil(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    const pad = (n) => n.toString().padStart(2, '0');
    return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
  };


  // useEffect(() => {
  //   const id = setInterval(() => {
  //     const now = Date.now();
  //     const diff = new Date(retakeAvailableAt).getTime() - now;
  //     if (diff <= 0) {
  //       clearInterval(id);
  //     }
  //   }, 1000);
  //   return () => clearInterval(id);
  // }, [retakeAvailableAt]);
  
  // const formatDuration = (ms) => {
  //   if (ms < 0) ms = 0; // Prevent negative countdown
  //   const totalSeconds = Math.ceil(ms / 1000);
  //   const hours = Math.floor(totalSeconds / 3600);
  //   const minutes = Math.floor((totalSeconds % 3600) / 60);
  //   const seconds = totalSeconds % 60;
  //   const pad = (n) => n.toString().padStart(2, '0');
  //   return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
  // };
  

  const loadQuestions = async () => {
    if (!courseName || !studentUid) return;
    
    setQuestionsLoading(true);
    try {
      console.log('Loading questions for:', { courseName, studentUid });
      
      // Load all quiz questions for this course
      const q = query(collection(firestore, 'quizzes'), where('courseName', '==', courseName));
      const snapshot = await getDocs(q);
      const allQuestions = snapshot.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() }));

      console.log('All questions loaded:', allQuestions.length);
      allQuestions.forEach(q => {
        console.log('Question data:', {
          id: q.id,
          question: q.question,
          correctAnswer: q.correctAnswer,
          correctAnswerType: typeof q.correctAnswer,
          options: q.options,
          quizName: q.quizName,
          courseName: q.courseName
        });
      });

      // Group questions by quiz name
      const quizzesByName = {};
      for (const question of allQuestions) {
        const name = question.quizName || `${courseName} Quiz`;
        if (!quizzesByName[name]) quizzesByName[name] = [];
        quizzesByName[name].push(question);
      }

      console.log('Quizzes grouped by name:', Object.keys(quizzesByName));
      Object.entries(quizzesByName).forEach(([name, questions]) => {
        console.log(`Quiz "${name}":`, questions.length, 'questions');
      });

      const allQuizNames = Object.keys(quizzesByName);
      if (allQuizNames.length === 0) {
        setQuestions([]);
        setQuestionsLoading(false);
        return;
      }

      // Get student's quiz results to check completion status
      const quizResultsQuery = query(
        collection(firestore, 'quizResults'),
        where('studentId', '==', studentUid),
        where('courseName', '==', courseName)
      );
      const quizResultsSnap = await getDocs(quizResultsQuery);
      const quizResults = quizResultsSnap.docs.map(doc => doc.data());
      
      // Get passed quizzes and failed quizzes
      const passedQuizNames = new Set(
        quizResults.filter(result => result.isPassed).map(result => result.quizName).filter(Boolean)
      );
      const failedQuizNames = new Set(
        quizResults.filter(result => !result.isPassed).map(result => result.quizName).filter(Boolean)
      );

      console.log('Student passed quizzes:', Array.from(passedQuizNames));
      console.log('Student failed quizzes:', Array.from(failedQuizNames));
      console.log('Available quiz names:', allQuizNames);

      // Sort quiz names to ensure consistent order (sequential completion)
      const sortedQuizNames = allQuizNames.sort();
      
      let assignedQuizName = null;
      
      // Check if student has any failed quiz that needs to be completed first
      const failedQuizInOrder = sortedQuizNames.find(name => failedQuizNames.has(name));
      
      if (failedQuizInOrder) {
        // Student must complete the failed quiz first before moving to next
        assignedQuizName = failedQuizInOrder;
        console.log('🚫 SEQUENTIAL QUIZ ENFORCEMENT: Student must complete failed quiz first:', assignedQuizName);
        console.log('🔒 Student is STUCK on this quiz until they pass it');
      } else {
        // Find the first quiz that hasn't been passed yet (sequential order)
        const nextQuizToComplete = sortedQuizNames.find(name => !passedQuizNames.has(name));
        
        if (nextQuizToComplete) {
          assignedQuizName = nextQuizToComplete;
          console.log('✅ SEQUENTIAL QUIZ PROGRESSION: Assigned next sequential quiz:', assignedQuizName);
          console.log('🎯 Student can attempt this quiz because no failed quizzes are blocking them');
        } else {
          // All quizzes passed - allow retaking any quiz
          assignedQuizName = sortedQuizNames[Math.floor(Math.random() * sortedQuizNames.length)];
          console.log('🏆 ALL QUIZZES PASSED: Allowing retake of:', assignedQuizName);
        }
      }

      // Fallback safety
      if (!assignedQuizName || !quizzesByName[assignedQuizName]) {
        assignedQuizName = allQuizNames[0];
      }

      const selectedQuiz = quizzesByName[assignedQuizName];
      setQuestions(selectedQuiz);
      setQuizName(assignedQuizName);
      console.log(`Assigned quiz "${assignedQuizName}" to student UID: ${studentUid}`);
    } catch (error) {
      console.error('Error loading questions:', error);
      showError('Failed to load quiz questions');
    }
    setQuestionsLoading(false);
  };

  const checkPreviousAttempts = async () => {
    if (!studentUid || !courseName || !quizName) return;
    
    try {
      // Only check for attempts of the CURRENT assigned quiz, not all quizzes
      const q = query(
        collection(firestore, 'quizResults'),
        where('studentId', '==', studentUid),
        where('courseName', '==', courseName),
        where('quizName', '==', quizName) // Only check current quiz
      );
      const snapshot = await getDocs(q);
      
      console.log('Checking previous attempts for current quiz:', quizName);
      console.log('Found attempts for current quiz:', snapshot.docs.length);
      
      if (!snapshot.empty) {
        const attempts = snapshot.docs.map(d => d.data());
        // Sort by submittedAt desc (ISO string sorts lexicographically)
        attempts.sort((a, b) => (b.submittedAt || '').localeCompare(a.submittedAt || ''));
        const lastAttempt = attempts[0];

        console.log('Last attempt for current quiz:', lastAttempt);

        // Only show completed state if there's a recent attempt with cooldown
        if (lastAttempt && lastAttempt.isPassed === false && lastAttempt.submittedAt) {
          const nextTime = new Date(new Date(lastAttempt.submittedAt).getTime() +  24 * 60 * 60 * 1000).toISOString();
          if (Date.now() < new Date(nextTime).getTime()) {
            console.log('Cooldown active, showing last attempt results');
            setResults(lastAttempt);
            setQuizCompleted(true);
            setRetakeAvailableAt(nextTime);
          } else {
            console.log('Cooldown expired, allowing new attempt');
            setRetakeAvailableAt(null);
            setQuizCompleted(false);
            setResults(null);
          }
        } else {
          console.log('No cooldown needed, allowing new attempt');
          setRetakeAvailableAt(null);
          setQuizCompleted(false);
          setResults(null);
        }
      } else {
        console.log('No previous attempts for current quiz, allowing fresh attempt');
        setRetakeAvailableAt(null);
        setQuizCompleted(false);
        setResults(null);
      }
    } catch (error) {
      console.error('Error checking previous attempts:', error);
    }
  };

  const startQuiz = () => {
    setQuizStarted(true);
    setCurrentQuestionIndex(0);
    setAnswers({});
    setQuizCompleted(false);
    setResults(null);
    setTimeLeft(30);
    setShowAnswerFeedback(false);
    setSelectedAnswer(null);
  };

  const handleTimeUp = () => {
    const currentQuestion = questions[currentQuestionIndex];
    // Mark as wrong answer (null) when time runs out
    setAnswers(prev => ({
      ...prev,
      [currentQuestion.id]: null
    }));
    
    // Show feedback briefly then move to next question
    setShowAnswerFeedback(true);
    setIsAnswerCorrect(false);
    setSelectedAnswer(null);
    
    setTimeout(() => {
      if (currentQuestionIndex < questions.length - 1) {
        setCurrentQuestionIndex(currentQuestionIndex + 1);
      } else {
        // Last question - submit quiz
        submitQuiz();
      }
    }, 3000);
  };

  const handleAnswerSelect = (questionId, selectedOption) => {
    const currentQuestion = questions[currentQuestionIndex];
    
    // Robust comparison with proper type conversion
    let isCorrect = false;
    const selectedOptionNum = Number(selectedOption);
    const correctAnswerNum = Number(currentQuestion.correctAnswer);
    
    if (!isNaN(selectedOptionNum) && !isNaN(correctAnswerNum)) {
      isCorrect = selectedOptionNum === correctAnswerNum;
    } else {
      // Fallback to string comparison
      isCorrect = String(selectedOption) === String(currentQuestion.correctAnswer);
    }
    
    console.log('Answer Selection Debug:', {
      questionId,
      selectedOption,
      selectedOptionType: typeof selectedOption,
      correctAnswer: currentQuestion.correctAnswer,
      correctAnswerType: typeof currentQuestion.correctAnswer,
      selectedOptionNum,
      correctAnswerNum,
      isCorrect
    });
    
    // Save answer immediately and wait for state update
    const newAnswers = {
      ...answers,
      [questionId]: selectedOption
    };
    
    setAnswers(newAnswers);
    console.log('Updated answers object:', newAnswers);
    
    // Show feedback
    setShowAnswerFeedback(true);
    setIsAnswerCorrect(isCorrect);
    setSelectedAnswer(selectedOption);
    
    // Move to next question after 3 seconds
    setTimeout(() => {
      if (currentQuestionIndex < questions.length - 1) {
        setCurrentQuestionIndex(currentQuestionIndex + 1);
      } else {
        // Last question - submit quiz with updated answers
        console.log('Last question answered, submitting with answers:', newAnswers);
        // Pass the updated answers directly to avoid race condition
        submitQuiz(newAnswers);
      }
    }, 3000);
  };

  const nextQuestion = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  const previousQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const submitQuiz = async (providedAnswers = null) => {
    if (!studentUid) {
      showError('Please log in to submit the quiz');
      return;
    }

    // Use provided answers or fall back to state answers
    const finalAnswers = providedAnswers || answers;

    setLoading(true);
    try {
      console.log('=== QUIZ SUBMISSION START ===');
      console.log('Questions:', questions);
      console.log('Student Answers (from state):', answers);
      console.log('Provided Answers:', providedAnswers);
      console.log('Final Answers (used for calculation):', finalAnswers);
      console.log('Student UID:', studentUid);
      console.log('Course Name:', courseName);
      
      // ROBUST SCORING LOGIC with proper type handling
      let correctAnswers = 0;
      const questionResults = [];
      
      console.log('=== ROBUST SCORING LOGIC ===');

      console.log('=== DETAILED SCORING DEBUG ===');
      console.log('Total questions to process:', questions.length);
      console.log('Final Answers object for scoring:', finalAnswers);
      
      for (let i = 0; i < questions.length; i++) {
        const question = questions[i];
        const studentAnswer = finalAnswers[question.id];
        
        console.log(`\n--- Processing Question ${i + 1} ---`);
        console.log('Question ID:', question.id);
        console.log('Student Answer Raw:', studentAnswer);
        console.log('Correct Answer Raw:', question.correctAnswer);
        
        // Robust comparison with proper type conversion and null handling
        let isCorrect = false;
        
        // Handle null/undefined answers (time up or not answered)
        if (studentAnswer === null || studentAnswer === undefined) {
          console.log('Answer is null/undefined - marking as incorrect');
          isCorrect = false;
        } else {
          // Convert both to numbers for comparison to handle type mismatches
          const studentAnswerNum = Number(studentAnswer);
          const correctAnswerNum = Number(question.correctAnswer);
          
          console.log('Student Answer Number:', studentAnswerNum);
          console.log('Correct Answer Number:', correctAnswerNum);
          console.log('Student Answer isNaN:', isNaN(studentAnswerNum));
          console.log('Correct Answer isNaN:', isNaN(correctAnswerNum));
          
          // Check if both are valid numbers
          if (!isNaN(studentAnswerNum) && !isNaN(correctAnswerNum)) {
            isCorrect = studentAnswerNum === correctAnswerNum;
            console.log('Using number comparison:', isCorrect);
          } else {
            // Fallback to string comparison if number conversion fails
            isCorrect = String(studentAnswer) === String(question.correctAnswer);
            console.log('Using string comparison:', isCorrect);
          }
        }
        
        console.log('Final isCorrect result:', isCorrect);
        console.log('Correct answers before increment:', correctAnswers);
        
        if (isCorrect) {
          correctAnswers++;
          console.log('✅ Answer is correct! Incrementing count to:', correctAnswers);
        } else {
          console.log('❌ Answer is incorrect. Count remains:', correctAnswers);
        }

        console.log(`Question ${i + 1} Summary:`, {
          question: question.question,
          studentAnswer: studentAnswer,
          studentAnswerType: typeof studentAnswer,
          correctAnswer: question.correctAnswer,
          correctAnswerType: typeof question.correctAnswer,
          studentAnswerNum: Number(studentAnswer),
          correctAnswerNum: Number(question.correctAnswer),
          isCorrect: isCorrect,
          runningTotal: correctAnswers
        });

        questionResults.push({
          questionId: question.id || '',
          question: question.question || '',
          studentAnswer: studentAnswer !== null && studentAnswer !== undefined ? studentAnswer : 'Not answered',
          correctAnswer: question.correctAnswer !== null && question.correctAnswer !== undefined ? question.correctAnswer : '',
          isCorrect: isCorrect,
          options: question.options || []
        });
      }

      // CALCULATION: (correctAnswers / totalQuestions) * 100
      const totalQuestions = questions.length;
      const percentage = totalQuestions > 0 ? (correctAnswers / totalQuestions) * 100 : 0;
      const score = Math.round(percentage);
      const isPassed = score >= 70;

      console.log('=== FINAL CALCULATION DEBUG ===');
      console.log('Total Questions:', totalQuestions);
      console.log('Correct Answers:', correctAnswers);
      console.log('Raw Percentage:', percentage);
      console.log('Final Score:', score);
      console.log('Is Passed (score >= 70):', isPassed);
      console.log('Score type:', typeof score);
      console.log('Correct Answers type:', typeof correctAnswers);
      console.log('Questions length type:', typeof totalQuestions);

      // Save results with proper null/undefined handling
      const resultData = {
        studentId: studentUid || '',
        studentEmail: user?.email || '',
        courseName: courseName || '',
        course: courseName || '',
        score: isNaN(score) ? 0 : score,
        percentage: isNaN(percentage) ? 0 : percentage,
        passingPercentage: 70,
        isPassed: isPassed || false,
        totalQuestions: questions.length || 0,
        correctAnswers: correctAnswers || 0,
        questionResults: questionResults || [],
        submittedAt: new Date().toISOString(),
        answers: finalAnswers || {},
        quizName: quizName || `${courseName || 'Unknown'} Quiz`
      };

      // Validate required fields before submission
      if (!resultData.studentId) {
        throw new Error('Student ID is required');
      }
      if (!resultData.courseName) {
        throw new Error('Course name is required');
      }
      if (resultData.totalQuestions === 0) {
        throw new Error('No questions found');
      }

      // Sanitize data to remove all undefined values recursively
      const sanitizeData = (obj) => {
        if (obj === null || obj === undefined) return null;
        if (typeof obj !== 'object') return obj;
        if (Array.isArray(obj)) {
          return obj.map(item => sanitizeData(item)).filter(item => item !== undefined);
        }
        
        const sanitized = {};
        for (const [key, value] of Object.entries(obj)) {
          if (value !== undefined) {
            const sanitizedValue = sanitizeData(value);
            if (sanitizedValue !== undefined) {
              sanitized[key] = sanitizedValue;
            }
          }
        }
        return sanitized;
      };

      const sanitizedResultData = sanitizeData(resultData);
      console.log('Saving sanitized result data:', sanitizedResultData);

      // Attempt to save to Firestore with enhanced error handling
      try {
        const docRef = await addDoc(collection(firestore, 'quizResults'), sanitizedResultData);
        console.log('Quiz result saved successfully with ID:', docRef.id);
        
        setResults(resultData);
        setQuizCompleted(true);
        showSuccess(`Quiz completed! Your score: ${score}%`);

        // Call the callback to refresh quiz statistics
        if (onQuizCompleted) {
          onQuizCompleted();
        }

        // Navigate back after showing results
        setTimeout(() => {
          window.location.href = '/student-dashboard';
        }, 3000);

        // Set cooldown if failed
        if (!isPassed) {
          const nextTime = new Date(Date.now() +  24 * 60 * 60 * 1000).toISOString();
          setRetakeAvailableAt(nextTime);
        } else {
          setRetakeAvailableAt(null);
        }
      } catch (firestoreError) {

        
        // Try to identify the problematic field
        for (const [key, value] of Object.entries(resultData)) {
          if (value === undefined) {
            console.error(`Undefined field detected: ${key}`);
          }
        }
        
        throw new Error(`Firestore error: ${firestoreError.message}`);
      }
    } catch (error) {
      console.error('Error submitting quiz:', error);
      showError(`Failed to submit quiz: ${error.message}. Please try again.`);
    }
    setLoading(false);
  };

  const retakeQuiz = () => {
    // Block if cooldown active
    if (retakeAvailableAt && Date.now() < new Date(retakeAvailableAt).getTime()) {
      return;
    }
    setQuizStarted(false);
    setQuizCompleted(false);
    setResults(null);
    setAnswers({});
    setCurrentQuestionIndex(0);
  };

  // Test function to verify calculation logic
  const testCalculation = () => {
    console.log('=== TESTING QUIZ CALCULATION ===');
    const testQuestions = [
      { id: '1', correctAnswer: 0, question: 'Test 1' },
      { id: '2', correctAnswer: 1, question: 'Test 2' }
    ];
    const testAnswers = { '1': 0, '2': 1 }; // Both correct
    
    let correctAnswers = 0;
    testQuestions.forEach(question => {
      const studentAnswer = testAnswers[question.id];
      const studentAnswerNum = Number(studentAnswer);
      const correctAnswerNum = Number(question.correctAnswer);
      const isCorrect = studentAnswerNum === correctAnswerNum;
      if (isCorrect) correctAnswers++;
      
      console.log(`Test Question ${question.id}:`, {
        studentAnswer,
        studentAnswerNum,
        correctAnswer: question.correctAnswer,
        correctAnswerNum,
        isCorrect,
        types: { student: typeof studentAnswer, correct: typeof question.correctAnswer }
      });
    });
    
    const percentage = (correctAnswers / testQuestions.length) * 100;
    console.log('Test Result:', { correctAnswers, total: testQuestions.length, percentage, isPassed: percentage >= 70 });
  };

  // Run test on component mount
  useEffect(() => {
    testCalculation();
  }, []);

  if (questionsLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <svg className="w-8 h-8 animate-spin text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
        </svg>
      </div>
    );
  }

  if (questions.length === 0) {
    return (
      <div className="text-center py-8">
        <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <h3 className="text-lg font-semibold text-gray-700 mb-2">No Quiz Available</h3>
        <p className="text-gray-500">There are no quiz questions available for this course yet.</p>
      </div>
    );
  }

  if (quizCompleted && results) {
    return (
      <div className="max-w-3xl mx-auto">
        <div className="bg-gradient-to-br from-purple-50 to-orange-50 rounded-xl shadow-lg p-6 mb-6 border border-purple-200">
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-orange-500 bg-clip-text text-transparent mb-2">{quizName || 'Quiz'} Results</h2>
            <p className="text-purple-700">Course: {courseName}</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-gradient-to-br from-purple-100 to-purple-200 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-purple-700">{results.score}%</div>
              <div className="text-sm text-purple-600">Score</div>
            </div>
            <div className="bg-gradient-to-br from-orange-100 to-orange-200 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-orange-700">{results.correctAnswers}/{results.totalQuestions}</div>
              <div className="text-sm text-orange-600">Correct Answers</div>
            </div>
            <div className="bg-gradient-to-br from-purple-200 to-orange-200 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-purple-600">{results.totalQuestions - results.correctAnswers}</div>
              <div className="text-sm text-purple-500">Wrong Answers</div>
            </div>
            <div className={`rounded-lg p-4 text-center ${results.isPassed ? 'bg-gradient-to-br from-orange-100 to-orange-200' : 'bg-gradient-to-br from-purple-100 to-purple-200'}`}>
              <div className={`text-2xl font-bold ${results.isPassed ? 'text-orange-700' : 'text-purple-700'}`}>
                {results.isPassed ? 'PASSED' : 'FAILED'}
              </div>
              <div className={`text-sm ${results.isPassed ? 'text-orange-600' : 'text-purple-600'}`}>
                Required: {results.passingPercentage}%
              </div>
            </div>
          </div>

          <div className="text-center">
            {results.isPassed ? (
              <button
                onClick={() => window.location.href = '/student-dashboard'}
                className="bg-gradient-to-r from-orange-500 to-orange-600 text-white px-6 py-2 rounded-lg hover:from-orange-600 hover:to-orange-700 transition-all duration-300 shadow-lg"
              >
                Next Quiz
              </button>
            ) : (
              <div className="flex flex-col items-center gap-2">
               <button
                 onClick={retakeQuiz}
                  disabled={retakeAvailableAt && retakeRemainingMs > 0}
                  className={`px-6 py-2 rounded-lg transition-all duration-300 text-white shadow-lg ${
                    retakeAvailableAt && retakeRemainingMs > 0
                      ? 'bg-gradient-to-r from-purple-300 to-purple-400 cursor-not-allowed'
                      : 'bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700'
                  }`}
               >
                 Retake Quiz
               </button>
                {retakeAvailableAt && retakeRemainingMs > 0 && (
                  <div className="text-sm text-purple-600">
                    Retake available in {formatDuration(retakeRemainingMs)}
                  </div>
                )}
              </div>
            )}
           </div>
        </div>
      </div>
    );
  }

  if (!quizStarted) {
    return (
      <div className="max-w-md mx-auto">
        <div className="bg-gradient-to-br from-purple-50 to-orange-50 rounded-xl shadow-lg p-6 text-center border border-purple-200">
          <div className="w-16 h-16 bg-gradient-to-br from-purple-100 to-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          
          <h2 className="text-xl font-bold bg-gradient-to-r from-purple-600 to-orange-500 bg-clip-text text-transparent mb-2">{quizName || 'Course Quiz'}</h2>
          <p className="text-purple-700 mb-4">Test your knowledge with {questions.length} multiple choice questions</p>
          
          <div className="bg-gradient-to-r from-purple-100 to-orange-100 rounded-lg p-4 mb-6">
            <div className="text-sm text-purple-700 space-y-1">
              <p>• {questions.length} questions</p>
              <p>• Multiple choice format</p>
              <p>• You can review your answers before submitting</p>
            </div>
          </div>
          
          <div className="space-y-3">
            <button
              onClick={startQuiz}
              className="w-full bg-gradient-to-r from-purple-600 to-orange-500 text-white py-3 px-4 rounded-lg hover:from-purple-700 hover:to-orange-600 transition-all duration-300 font-semibold shadow-lg"
            >
              Start Quiz
            </button>
            
            {/* <button
              onClick={() => {
                console.log('=== TESTING CURRENT QUESTIONS ===');
                questions.forEach((q, i) => {
                  console.log(`Question ${i + 1}:`, {
                    question: q.question,
                    correctAnswer: q.correctAnswer,
                    correctAnswerType: typeof q.correctAnswer,
                    options: q.options
                  });
                });
              }}
              className="w-full bg-gradient-to-r from-purple-400 to-orange-400 text-white py-2 px-4 rounded-lg hover:from-purple-500 hover:to-orange-500 transition-all duration-300 text-sm shadow-md"
            >
              Debug Questions
            </button> */}
          </div>
        </div>
      </div>
    );
  }

  const currentQuestion = questions[currentQuestionIndex];
  const currentSelectedAnswer = answers[currentQuestion.id];

  return (
    <div className="max-w-2xl mx-auto">
      <div className="bg-gradient-to-br from-purple-50 to-orange-50 rounded-xl shadow-lg p-6 border border-purple-200">
        {/* Question Counter and Timer */}
        <div className="mb-6">
          <div className="flex justify-between items-center mb-4">
            <div className="text-center flex-1">
              <h2 className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-orange-500 bg-clip-text text-transparent">
                Question {currentQuestionIndex + 1} of {questions.length}
              </h2>
              <p className="text-sm text-purple-600">Quiz Progress</p>
            </div>
            <div className="text-center">
              <div className={`text-3xl font-bold ${timeLeft <= 10 ? 'text-red-600' : 'text-orange-600'}`}>
                {timeLeft}s
              </div>
              <p className="text-sm text-purple-600">Time Left</p>
            </div>
          </div>
          
          {/* Progress Bar */}
          <div className="w-full bg-gray-200 rounded-full h-2 mb-4">
            <div
              className="bg-gradient-to-r from-purple-600 to-orange-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${((currentQuestionIndex + 1) / questions.length) * 100}%` }}
            ></div>
          </div>
          
          {/* Timer Bar */}
          <div className="w-full bg-gray-200 rounded-full h-1">
            <div
              className={`h-1 rounded-full transition-all duration-1000 ${
                timeLeft <= 10 ? 'bg-red-500' : 'bg-green-500'
              }`}
              style={{ width: `${(timeLeft / 30) * 100}%` }}
            ></div>
          </div>
        </div>

        {/* Question */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold bg-gradient-to-r from-purple-700 to-orange-600 bg-clip-text text-transparent mb-4">
            {currentQuestion.question}
          </h3>
          
          <div className="space-y-3">
            {currentQuestion.options.map((option, index) => {
              const isSelected = currentSelectedAnswer === index;
              const isCorrect = index === currentQuestion.correctAnswer;
              const isWrong = isSelected && !isCorrect;
              
              let optionStyle = 'border-gray-200 hover:border-gray-300';
              if (showAnswerFeedback) {
                if (isWrong) {
                  optionStyle = 'border-red-500 bg-red-50';
                }
              } else if (isSelected) {
                optionStyle = 'border-purple-500 bg-purple-50';
              }
              
              return (
                <label
                  key={index}
                  className={`flex items-center p-3 border rounded-lg transition-colors ${
                    showAnswerFeedback ? 'cursor-default' : 'cursor-pointer'
                  } ${optionStyle}`}
                >
                  <input
                    type="radio"
                    name={`question-${currentQuestion.id}`}
                    value={index}
                    checked={isSelected}
                    onChange={() => !showAnswerFeedback && handleAnswerSelect(currentQuestion.id, index)}
                    disabled={showAnswerFeedback}
                    className="sr-only"
                  />
                  <span className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mr-3 ${
                    showAnswerFeedback && isWrong
                      ? 'border-red-500 bg-red-500'
                      : isSelected
                      ? 'border-purple-500 bg-purple-500'
                      : 'border-gray-300'
                  }`}>
                    {isSelected && (
                      <div className="w-2 h-2 bg-white rounded-full"></div>
                    )}
                  </span>
                  <span className={`${
                    showAnswerFeedback && isWrong
                      ? 'text-red-800 font-semibold'
                      : 'text-gray-700'
                  }`}>
                    {option}
                  </span>
                  {showAnswerFeedback && isWrong && (
                    <span className="ml-auto text-red-600">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </span>
                  )}
                </label>
              );
            })}
          </div>
        </div>

        {/* Feedback Message */}
        {showAnswerFeedback && (
          <div className="mt-6 p-4 rounded-lg text-center">
            {isAnswerCorrect ? (
              <div className="text-green-700 bg-green-50 border border-green-200 rounded-lg p-3">
                <div className="flex items-center justify-center gap-2">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="font-semibold">Correct!</span>
                </div>
                <p className="text-sm mt-1">Moving to next question...</p>
              </div>
            ) : (
              <div className="text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">
                <div className="flex items-center justify-center gap-2">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  <span className="font-semibold">
                    {selectedAnswer !== null ? 'Incorrect!' : 'Time\'s up!'}
                  </span>
                </div>
                <p className="text-sm mt-1">Moving to next question...</p>
              </div>
            )}
          </div>
        )}


      </div>
    </div>
  );
};

export default StudentQuiz;
